// Sits MathJax's output level with the text around it.
//
// A formula reads as centred when its math axis — where "+" and a fraction bar rest, 0.25em above the
// math baseline — lands on the surrounding text's optical centre, half an x-height above the text
// baseline. Neither term depends on what the formula contains, so one rule holds everywhere. That is
// the advantage over `vertical-align: middle`, which centres the container's box: the box grows with
// the tallest construct in the formula, so the leftover correction differs per formula and per font.
//
// The x-height has to be measured rather than taken from CSS `ex`. `ex` reports the font's declared
// sxHeight, and TTC skin display fonts get it badly wrong — Rye declares 0.19em and draws 0.56em,
// which left the out-of-time bar's formula about 3px low. A canvas reports what is actually drawn.

const MATH_AXIS_HEIGHT = 0.25

// What CSS itself falls back to for `ex` when a font declares no x-height. Used here for the reason.
const FALLBACK_X_HEIGHT_RATIO = 0.5

// Measuring at a large size keeps glyph hinting from coarsening the ratio.
const MEASURE_FONT_SIZE = 100

let measureContext: CanvasRenderingContext2D | null | undefined

const getMeasureContext = (): CanvasRenderingContext2D | null => {
	if (measureContext === undefined) measureContext = document.createElement('canvas').getContext('2d')
	return measureContext
}

// Measured on every call rather than cached per face. A cached ratio taken before a skin webfont landed
// would be the fallback face's, and wrong for the rest of the session; measuring each time costs
// microseconds and self-corrects on the next typeset.
//
// 'x' is the glyph to probe: it has a flat top in effectively every face, so there is no overshoot to
// subtract, unlike the rounded 'o' or 'e'.
const measureXHeightRatio = (style: string, weight: string, family: string): number => {
	const context = getMeasureContext()
	if (!context) return FALLBACK_X_HEIGHT_RATIO

	context.font = `${style} ${weight} ${MEASURE_FONT_SIZE}px ${family}`
	const ascent = context.measureText('x').actualBoundingBoxAscent

	return typeof ascent === 'number' && ascent > 0 ? ascent / MEASURE_FONT_SIZE : FALLBACK_X_HEIGHT_RATIO
}

// Writes the offset inline, overriding the CSS fallback in RichTextContent.css. Safe to call again on
// the same subtree; MathJax does not set the container's vertical-align for inline formulas itself.
const alignFormulasToText = (root: HTMLElement): void => {
	root.querySelectorAll<HTMLElement>('mjx-container').forEach((container) => {
		const text = container.parentElement
		if (!text) return

		const textStyle = window.getComputedStyle(text)
		const textSize = parseFloat(textStyle.fontSize)
		// MathJax scales the formula by writing font-size on the container, so this is the em the math
		// axis is expressed in, not the surrounding text's.
		const mathSize = parseFloat(window.getComputedStyle(container).fontSize)
		if (!textSize || !mathSize) return

		const xHeight = measureXHeightRatio(textStyle.fontStyle, textStyle.fontWeight, textStyle.fontFamily) * textSize
		const offset = xHeight / 2 - MATH_AXIS_HEIGHT * mathSize

		container.style.verticalAlign = `${offset.toFixed(2)}px`
	})
}

export default alignFormulasToText
